import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import {
  BROWSE_FILTER_ASSET_TYPE_NAMES,
  browseCanonicalEnglishLabel,
} from '../constants/filterableAssetTypes';
import { localizeAssetTypeLabel, useI18n, type MessageKey } from '../i18n';
import { AssetTypeIcon } from './AssetTypeIcon';
import { HorizontalCarousel } from './HorizontalCarousel';
import styles from './AssetTypeDesktopFlipCarousel.module.css';

/**
 * Offizz homepage flip-box photos (`public/home-categories/`).
 * Keys are {@link browseCanonicalEnglishLabel} English browse names.
 */
const CATEGORY_IMAGE_BY_ENGLISH: Readonly<Record<string, string>> = {
  'Private Office': '/home-categories/private-office.jpg',
  'Open Space': '/home-categories/open-space.jpg',
  'Conference Room': '/home-categories/conference-room.jpg',
  Gym: '/home-categories/gym.jpg',
  'Parking Garage': '/home-categories/parking.jpg',
  'Podcast Studio': '/home-categories/podcast-studio.png',
  Clinic: '/home-categories/clinic.jpg',
  'Business Lounge': '/home-categories/business-lounge.jpg',
  'Lecture Room': '/home-categories/lecture-room.png',
  Classroom: '/home-categories/classroom.jpg',
  Workshop: '/home-categories/workshop.jpg',
};

/** Optional back-face photo (defaults to front image). */
const CATEGORY_BACK_IMAGE_BY_ENGLISH: Readonly<Record<string, string>> = {
  'Private Office': '/home-categories/private-office-back.jpg',
  'Open Space': '/home-categories/open-space-back.png',
  'Conference Room': '/home-categories/conference-room-back.jpg',
  Gym: '/home-categories/gym-back.jpg',
  'Parking Garage': '/home-categories/parking-back.jpg',
  'Podcast Studio': '/home-categories/podcast-studio-back.png',
  Clinic: '/home-categories/clinic-back.jpg',
  'Business Lounge': '/home-categories/business-lounge-back.jpg',
  'Lecture Room': '/home-categories/lecture-room-back.png',
  Classroom: '/home-categories/classroom-back.webp',
  Workshop: '/home-categories/workshop-back.png',
};

/** Browse categories with no Offizz flip photo yet. */
export const BROWSE_CATEGORIES_MISSING_FLIP_IMAGE = [] as const;

const CATEGORY_CTA_KEY_BY_ENGLISH: Readonly<
  Record<(typeof BROWSE_FILTER_ASSET_TYPE_NAMES)[number], MessageKey>
> = {
  'Private Office': 'home.categoryFlipCta.privateOffice',
  'Open Space': 'home.categoryFlipCta.openSpace',
  'Conference Room': 'home.categoryFlipCta.conferenceRoom',
  Gym: 'home.categoryFlipCta.gym',
  'Parking Garage': 'home.categoryFlipCta.parking',
  'Podcast Studio': 'home.categoryFlipCta.podcastStudio',
  'Lecture Room': 'home.categoryFlipCta.lectureRoom',
  Clinic: 'home.categoryFlipCta.clinic',
  'Business Lounge': 'home.categoryFlipCta.businessLounge',
  Classroom: 'home.categoryFlipCta.classroom',
  Workshop: 'home.categoryFlipCta.workshop',
};

type AssetTypeDesktopFlipCarouselProps = {
  types: GeneralInfoPicklistItem[];
  onSelectType: (type: GeneralInfoPicklistItem) => void;
};

/**
 * PC-only (≥768px) Offizz-style category flip carousel (hover reveals CTA).
 * Mobile keeps {@link AssetTypeStripCarousel}.
 */
export function AssetTypeDesktopFlipCarousel({
  types,
  onSelectType,
}: AssetTypeDesktopFlipCarouselProps) {
  const { t, language } = useI18n();

  if (types.length === 0) {
    return null;
  }

  return (
    <section className={styles.section} aria-labelledby="home-desktop-categories-title">
      <div className={styles.sectionHead}>
        <h2 id="home-desktop-categories-title" className={styles.sectionTitle}>
          {t('home.bookingCategoriesTitle')}
        </h2>
        <div className={styles.sectionRule} aria-hidden />
      </div>
      <HorizontalCarousel
        ariaLabel={t('home.browseByAssetType')}
        prevAriaLabel={t('home.prevAssetTypes')}
        nextAriaLabel={t('home.nextAssetTypes')}
        contentKey={`desktop-flip-${types.map((type) => type.id).join(',')}`}
        scrollClassName={styles.categoryScroll}
        trackItemClassName={styles.trackItem}
      >
        {types.map((type) => (
          <FlipTypeCard
            key={type.id}
            type={type}
            language={language}
            onSelect={() => onSelectType(type)}
          />
        ))}
      </HorizontalCarousel>
    </section>
  );
}

function FlipTypeCard({
  type,
  language,
  onSelect,
}: {
  type: GeneralInfoPicklistItem;
  language: 'en' | 'he';
  onSelect: () => void;
}) {
  const { t } = useI18n();
  const iconLabel = browseCanonicalEnglishLabel(type.label);
  const displayLabel = localizeAssetTypeLabel(type.label, language, t);
  const imageSrc = CATEGORY_IMAGE_BY_ENGLISH[iconLabel] ?? null;
  const backImageSrc = CATEGORY_BACK_IMAGE_BY_ENGLISH[iconLabel] ?? imageSrc;
  const ctaKey =
    CATEGORY_CTA_KEY_BY_ENGLISH[iconLabel as (typeof BROWSE_FILTER_ASSET_TYPE_NAMES)[number]] ??
    null;
  const ctaLabel = ctaKey ? t(ctaKey) : t('home.showAll');

  return (
    <button type="button" className={styles.flipCard} onClick={onSelect}>
      <span className={styles.flipInner}>
        <span className={[styles.face, styles.faceFront].join(' ')}>
          {imageSrc ? (
            <img className={styles.photo} src={imageSrc} alt="" loading="lazy" decoding="async" />
          ) : (
            <span className={styles.iconFallback}>
              <AssetTypeIcon label={iconLabel} className={styles.typeIcon} />
            </span>
          )}
          <span className={styles.overlayFront} aria-hidden />
          <span className={styles.faceTitle}>{displayLabel}</span>
        </span>
        <span className={[styles.face, styles.faceBack].join(' ')} aria-hidden>
          {backImageSrc ? (
            <img className={styles.photo} src={backImageSrc} alt="" loading="lazy" decoding="async" />
          ) : (
            <span className={styles.iconFallback}>
              <AssetTypeIcon label={iconLabel} className={styles.typeIcon} />
            </span>
          )}
          <span className={styles.overlayBack} aria-hidden />
          <span className={styles.ctaPill}>{ctaLabel}</span>
        </span>
      </span>
    </button>
  );
}
